import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Switch,
  type SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { getLoggedInAs } from '@realadvisor/auth-client';
import { Check, SlidersHorizontal } from 'lucide-react';

import type {
  DictionarySelectFragment,
  GetUserQuery,
} from '@graphql/__generated__/graphql';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useRefreshMeAfterModuleGrant } from '@modules/users/hooks/useRefreshMeAfterModuleGrant';
import { SwitchLabelText } from '@modules/users/user-overview/components/SwitchLabelText';
import { useCanViewSubscriptions } from '@modules/users/user-overview/hooks/useCanViewSubscriptions';
import {
  GET_USER,
  UPDATE_USER_PERMISSION_FLAGS,
  UPDATE_USER_SETTINGS,
} from '@modules/users/userQueries';
import { useLocale } from '@shared/hooks/locale';

const triggerButtonSx: SxProps = {
  width: 22,
  height: 22,
  color: 'text.secondary',
  '&:hover': { backgroundColor: 'action.hover' },
};

type ToggleableFlag =
  | 'is_admin'
  | 'can_edit_settings'
  | 'can_user_login_as'
  | 'has_free_crm_access';

type PermissionUser = Pick<
  NonNullable<GetUserQuery['users_by_pk']>,
  | 'id'
  | 'is_admin'
  | 'is_broker'
  | 'can_edit_settings'
  | 'can_user_login_as'
  | 'has_free_crm_access'
  | 'membership_type'
>;

type UserPermissionsMenuProps = {
  user: PermissionUser;
  membershipTypes: DictionarySelectFragment[];
};

const membershipKey = (value: string | null) => `membership:${value ?? 'none'}`;

export const UserPermissionsMenu = ({
  user,
  membershipTypes,
}: UserPermissionsMenuProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { onModuleGrantCompleted, refreshError, resetRefreshError } =
    useRefreshMeAfterModuleGrant(user.id);
  const canViewSubscriptions = useCanViewSubscriptions(user.id);
  const canEditPermissions =
    me?.is_admin === true &&
    me.can_edit_settings === true &&
    getLoggedInAs() == null;

  const [apolloError, setApolloError] = useState<ErrorLike | null>(null);
  const [flagsAnchor, setFlagsAnchor] = useState<HTMLElement | null>(null);
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const showMembershipType =
    user.is_broker || user.is_admin || user.membership_type != null;
  const freeCrmRelevant = ['FR', 'ES', 'IT'].includes(
    me?.tenant?.country_code ?? '',
  );

  const refetchQueries = [
    {
      query: GET_USER,
      variables: { id: user.id, includeSubscriptions: canViewSubscriptions },
    },
  ];

  const [updateUserSettings] = useMutation(UPDATE_USER_SETTINGS, {
    refetchQueries,
    awaitRefetchQueries: true,
    onError: setApolloError,
  });

  const [updatePermissionFlags] = useMutation(UPDATE_USER_PERMISSION_FLAGS, {
    refetchQueries,
    awaitRefetchQueries: true,
    onError: setApolloError,
  });

  const runWithPending = (key: string, promise: Promise<unknown>) => {
    setPending(prev => new Set(prev).add(key));
    void promise
      .catch(() => {})
      .finally(() =>
        setPending(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        }),
      );
  };

  const updateMembershipType = (value: string | null) => {
    runWithPending(
      membershipKey(value),
      updateUserSettings({
        variables: { id: user.id, user: { membership_type: value } },
      }),
    );
  };

  const toggleFlag = (flag: ToggleableFlag, currentValue: boolean) => {
    const next = !currentValue;
    const promise =
      flag === 'has_free_crm_access'
        ? updateUserSettings({
            variables: { id: user.id, user: { [flag]: next } },
          })
        : updatePermissionFlags({
            onCompleted:
              flag === 'is_admin' ? onModuleGrantCompleted : undefined,
            variables: {
              user_id: user.id,
              is_admin: flag === 'is_admin' ? next : undefined,
              can_edit_settings:
                flag === 'can_edit_settings' ? next : undefined,
              can_user_login_as:
                flag === 'can_user_login_as' ? next : undefined,
            },
          });
    runWithPending(flag, promise);
  };

  return (
    <>
      <Tooltip title={t('Edit permissions')}>
        <IconButton
          size="small"
          onClick={evt => setFlagsAnchor(evt.currentTarget)}
          sx={triggerButtonSx}
        >
          <SlidersHorizontal size={14} />
        </IconButton>
      </Tooltip>

      <MutationErrorSnackbar
        error={apolloError ?? refreshError}
        onClose={() => {
          setApolloError(null);
          resetRefreshError();
        }}
      />

      <Menu
        anchorEl={flagsAnchor}
        open={!!flagsAnchor}
        onClose={() => setFlagsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { minWidth: 280 } },
        }}
      >
        <ListSubheader sx={{ lineHeight: 1.5, py: 1, bgcolor: 'transparent' }}>
          {t('Permissions')}
        </ListSubheader>
        <PermissionSwitchItem
          label={t('Admin')}
          description={t('Full administrator with access to all settings')}
          checked={!!user.is_admin}
          loading={pending.has('is_admin')}
          disabled={!canEditPermissions}
          onChange={() => toggleFlag('is_admin', !!user.is_admin)}
        />
        {canEditPermissions && user.is_admin && (
          <PermissionSwitchItem
            label={t('Can edit settings')}
            description={t('Allowed to edit tenant-wide settings')}
            checked={!!user.can_edit_settings}
            loading={pending.has('can_edit_settings')}
            onChange={() =>
              toggleFlag('can_edit_settings', !!user.can_edit_settings)
            }
          />
        )}
        {canEditPermissions && (
          <PermissionSwitchItem
            label={t('Can login as')}
            description={t('Allowed to impersonate other users')}
            checked={!!user.can_user_login_as}
            loading={pending.has('can_user_login_as')}
            onChange={() =>
              toggleFlag('can_user_login_as', !!user.can_user_login_as)
            }
          />
        )}
        {freeCrmRelevant && (
          <PermissionSwitchItem
            label={t('Free CRM access')}
            description={t('Has free access to the CRM (no subscription)')}
            checked={!!user.has_free_crm_access}
            loading={pending.has('has_free_crm_access')}
            onChange={() =>
              toggleFlag('has_free_crm_access', !!user.has_free_crm_access)
            }
          />
        )}
        {showMembershipType && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <ListSubheader
              sx={{ lineHeight: 1.5, py: 1, bgcolor: 'transparent' }}
            >
              {t('Membership type')}
            </ListSubheader>
            <MembershipTypeItem
              label={t('None')}
              selected={!user.membership_type}
              loading={pending.has(membershipKey(null))}
              muted
              onClick={() => updateMembershipType(null)}
            />
            {membershipTypes.map(type => (
              <MembershipTypeItem
                key={type.id}
                label={type.label ?? type.name}
                selected={user.membership_type === type.name}
                loading={pending.has(membershipKey(type.name))}
                onClick={() => updateMembershipType(type.name)}
              />
            ))}
          </>
        )}
      </Menu>
    </>
  );
};

type PermissionSwitchItemProps = {
  label: string;
  description: string;
  checked: boolean;
  loading: boolean;
  disabled?: boolean;
  onChange: () => void;
};

const PermissionSwitchItem = ({
  label,
  description,
  checked,
  loading,
  disabled = false,
  onChange,
}: PermissionSwitchItemProps) => {
  const isDisabled = disabled || loading;
  return (
    <MenuItem
      onClick={onChange}
      disabled={isDisabled}
      sx={{ py: 0.75, alignItems: 'flex-start' }}
    >
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={checked}
            onChange={onChange}
            onClick={evt => evt.stopPropagation()}
            disabled={isDisabled}
            sx={{ mr: 1 }}
          />
        }
        label={<SwitchLabelText label={label} description={description} />}
        sx={{
          m: 0,
          width: '100%',
          alignItems: 'flex-start',
          '.MuiFormControlLabel-label': { flex: 1 },
        }}
        onClick={evt => evt.stopPropagation()}
      />
    </MenuItem>
  );
};

type MembershipTypeItemProps = {
  label: string;
  selected: boolean;
  loading: boolean;
  muted?: boolean;
  onClick: () => void;
};

const MembershipTypeItem = ({
  label,
  selected,
  loading,
  muted = false,
  onClick,
}: MembershipTypeItemProps) => (
  <MenuItem
    onClick={onClick}
    disabled={loading}
    selected={selected}
    sx={{ py: 0.75, gap: 1 }}
  >
    <Box
      sx={{
        width: 18,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: 'primary.main',
      }}
    >
      {loading ? (
        <CircularProgress size={14} />
      ) : (
        selected && <Check size={16} />
      )}
    </Box>
    <Typography
      variant="body2"
      sx={{
        fontWeight: selected ? 600 : 400,
        color: muted ? 'text.secondary' : 'text.primary',
      }}
    >
      {label}
    </Typography>
  </MenuItem>
);
